import React, { createContext, useContext, useState, useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";

type SnackbarSeverity = "success" | "error" | "info" | "warning";

interface SnackbarContextType {
    showSnackbar: (message: string, severity?: SnackbarSeverity) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error("useSnackbar must be used inside SnackbarProvider");
    }
    return context;
};

export const SnackbarProvider = ({ children }: { children: React.ReactNode }) => {
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info" as SnackbarSeverity,
    });

    const showSnackbar = useCallback((message: string, severity: SnackbarSeverity = "info") => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={30000}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                {/* The severity colours this, as it always should have. There was a
                    hardcoded gold background here, which painted every snackbar the same
                    regardless of severity -- an error read exactly like a success. It was
                    the old brand's accent, which is now whatever a clinic chooses, so it
                    could not stay in any case. */}
                <Alert severity={snackbar.severity} onClose={handleClose} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};